import * as Surplus from "surplus";
import { S } from "surplus";
import { URIResources } from "../.generated/R";
import { ButtonImg, IconMinus, IconPlus } from "../util/decorators";
import { Img } from "../util/primitive-wrappers";

export interface PixelCardAttributes {
    src: URIResources;
    alt: string | null;
    initialZoom?: number;
    width: number;
    height: number;
    children?: any;
}

export function ZoomImg(props: PixelCardAttributes & JSX.HTMLAttributes<HTMLImageElement>) {
    let padOffset = 0,
        raf = false,
        containerRef: HTMLDivElement | undefined,
        timer = 0;
    const zoom = S.data(props.initialZoom || 2),
        controlsVisible = S.data(false);

    listenForResize(); // Recalculate when the containing page changes
    setTimeout(() => zoom(S.sample(zoom))); // lazy fix because the margin isn't correctly set on mount.

    S(() => onUpdate());

    let div = (
        <div
            ref={containerRef}
            class="zoom-img-container"
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}>
            <div
                class="flexRow flexVCenter ptrEvNone flex-haround zoom-img--ctrl mw100"
                style={{
                    opacity: controlsVisible() ? "1" : "0",
                    fontSize: `${Math.min(13, (width() / 95) * 2)}rem`,
                }}>
                <ButtonImg
                    aria-label="decrease image size"
                    onClick={(ev) => setTimeout(() => zoom(Math.max(1, zoom() - 1)), 100)}>
                    <IconMinus />
                </ButtonImg>
                <ButtonImg
                    aria-label="increase image size"
                    onClick={(ev) => setTimeout(() => zoom(Math.min(5, zoom() + 1)), 100)}>
                    <IconPlus />
                </ButtonImg>
            </div>
            <div class="inner-img">
                <div
                    style={{
                        width: `${width() + padOffset}px`,
                        height: `${height() + padOffset}px`,
                    }}>
                    <Img alt={props.alt} src={props.src} width={width()} height={height()} />
                </div>
            </div>
        </div>
    );

    return { dom: div, onMount: () => onUpdate() };

    function handleOnMouseEnter(e: MouseEvent) {
        e.stopPropagation();
        controlsVisible(true);
    }

    function handleOnMouseLeave(e: MouseEvent) {
        e.stopPropagation();
        if (timer) clearTimeout(timer);
        timer = window.setTimeout(() => controlsVisible(false), 500);
    }

    function marginLeft() {
        if (!containerRef || !containerRef.parentElement) return 0;
        let outerDiv = (containerRef.parentElement as HTMLElement).parentElement as HTMLElement;
        let outerDivBBOX = outerDiv.getBoundingClientRect();
        let value: number;
        if (outerDivBBOX == null) {
            value = 0;
        } else if (width() + outerDivBBOX.left >= window.visualViewport.width) {
            value = -outerDivBBOX.left;
        } else if (outerDivBBOX.right > width()) {
            value = 0;
        } else if (width() > width() + outerDivBBOX.left + outerDivBBOX.right) {
            value = -outerDivBBOX.left;
        } else {
            value = -outerDivBBOX.left;
        }

        return Math.round(value) | 0;
    }

    function onUpdate(): null {
        let parentWidth = width() + padOffset;
        if (containerRef && containerRef.parentElement) {
            // not available on first render
            containerRef.parentElement.style.width = `${parentWidth}px`;
            containerRef.parentElement.style.marginLeft = `${marginLeft()}px`;
        }
        return null;
    }

    function listenForResize() {
        window.addEventListener("resize", () => {
            if (!raf) {
                raf = true;

                window.requestAnimationFrame(() => {
                    zoom(S.sample(zoom));
                    raf = false;
                });
            }
        });
    }

    function width() {
        return props.width * zoom();
    }

    function height() {
        return props.height * zoom();
    }
}

export interface ImgProps {
    src: URIResources;
    alt: string | null;
}
